import { useState, useMemo, useEffect } from "react";
import { authFetch } from "../lib/authFetch";
import { usePortailAuth } from "../lib/auth-context";
import { useCart } from "../lib/cart-context";
import {
  Search, X, ShoppingCart, Package, Loader2, Wrench,
  Minus, Plus, List, LayoutGrid, ZoomIn, ChevronDown, ShieldCheck, ShieldOff,
} from "lucide-react";

interface PieceItem {
  id: string;
  sku: string;
  nomFr: string;
  descriptionFr: string | null;
  type: string;
  imageUrl: string | null;
  robotsCompatibles: string[];
  prixAffiche: number | null;
  statutLabel: string;
  sousGarantie: boolean;
}

const TYPE_LABELS: Record<string, { label: string; color: string }> = {
  piece_detachee: { label: "Pièce détachée", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" },
  consommable:    { label: "Consommable",    color: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
  accessoire:     { label: "Accessoire",     color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  iot:            { label: "IoT",            color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
};

function usePieces() {
  const [data, setData] = useState<{ items: PieceItem[]; statutLabel: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    authFetch("/api/portail/catalogue?type=pieces")
      .then(r => r.ok ? r.json() : Promise.reject(new Error("Erreur chargement pièces")))
      .then(json => { if (!cancelled) setData(json); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { items: data?.items ?? [], statutLabel: data?.statutLabel ?? "", isLoading, error };
}

function formatPrix(p: number | null) {
  if (p === null) return "Prix sur demande";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(p);
}

function QtySelector({ qty, onChange }: { qty: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, qty - 1))}
        className="w-6 h-6 flex items-center justify-center rounded border border-border text-foreground hover:bg-secondary/60 transition-colors"
        data-testid="button-qty-minus"
      >
        <Minus className="w-3 h-3" />
      </button>
      <input
        type="number"
        min={1}
        value={qty}
        onChange={e => onChange(Math.max(1, parseInt(e.target.value) || 1))}
        className="w-9 text-center text-xs font-medium bg-background border border-border rounded py-0.5 focus:outline-none focus:ring-2 focus:ring-primary/30"
        data-testid="input-qty"
      />
      <button
        type="button"
        onClick={() => onChange(qty + 1)}
        className="w-6 h-6 flex items-center justify-center rounded border border-border text-foreground hover:bg-secondary/60 transition-colors"
        data-testid="button-qty-plus"
      >
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
}

function GarantieBadge({ sousGarantie }: { sousGarantie: boolean }) {
  if (sousGarantie) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border border-green-200/60 dark:border-green-700/40 flex-shrink-0">
        <ShieldCheck className="w-2.5 h-2.5" />
        Sous garantie
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-400 border border-gray-200/60 dark:border-zinc-700/40 flex-shrink-0">
      <ShieldOff className="w-2.5 h-2.5" />
      Hors garantie
    </span>
  );
}

function PieceRow({
  item, primary, onDetail,
}: {
  item: PieceItem; primary: string; onDetail: () => void;
}) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);

  const typeMeta = TYPE_LABELS[item.type] ?? { label: item.type, color: "bg-muted text-muted-foreground" };
  const comptables = item.robotsCompatibles ?? [];
  const visible = comptables.slice(0, 3);
  const surplus = comptables.length - 3;

  return (
    <div
      className="flex items-center gap-4 px-6 py-3 hover:bg-secondary/30 transition-colors cursor-pointer"
      onClick={onDetail}
      data-testid={`row-piece-${item.id}`}
    >
      <div
        className="rounded-lg bg-gray-50 dark:bg-zinc-800 flex-shrink-0 overflow-hidden border border-border flex items-center justify-center"
        style={{ width: 80, height: 80 }}
      >
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.nomFr} className="w-full h-full object-contain" />
        ) : (
          <Package className="w-6 h-6 text-muted-foreground/40" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-foreground truncate">{item.nomFr}</p>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${typeMeta.color}`}>
            {typeMeta.label}
          </span>
          <GarantieBadge sousGarantie={item.sousGarantie} />
        </div>
        <p className="text-xs text-muted-foreground font-mono">{item.sku}</p>

        {visible.length > 0 && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {visible.map(r => (
              <span key={r} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200/60 dark:border-blue-700/40">
                {r}
              </span>
            ))}
            {surplus > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">+{surplus}</span>
            )}
          </div>
        )}
      </div>

      <div className="text-right flex-shrink-0 min-w-[90px]">
        <p className={`text-sm font-semibold ${item.prixAffiche !== null ? "text-foreground" : "text-muted-foreground italic"}`}>
          {formatPrix(item.prixAffiche)}
        </p>
        {item.prixAffiche !== null && (
          <p className="text-[10px] text-muted-foreground">HT</p>
        )}
      </div>

      <div onClick={e => e.stopPropagation()}>
        <QtySelector qty={qty} onChange={setQty} />
      </div>

      <button
        onClick={ev => {
          ev.stopPropagation();
          addItem({
            productId: item.id,
            sku: item.sku,
            nomFr: item.nomFr,
            prixAffiche: item.prixAffiche,
            statutLabel: item.statutLabel,
            imageUrl: item.imageUrl,
          }, qty);
        }}
        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-opacity hover:opacity-90 whitespace-nowrap"
        style={{ backgroundColor: primary }}
        data-testid={`button-add-devis-piece-${item.id}`}
      >
        <ShoppingCart className="w-3.5 h-3.5" />
        Ajouter au devis
      </button>
    </div>
  );
}

function PieceCard({
  item, primary, onDetail,
}: {
  item: PieceItem; primary: string; onDetail: () => void;
}) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const typeMeta = TYPE_LABELS[item.type] ?? { label: item.type, color: "bg-muted text-muted-foreground" };

  return (
    <div
      className="bg-card border border-border rounded-xl overflow-hidden flex flex-col cursor-pointer hover:shadow-md hover:border-primary/30 transition-all"
      onClick={onDetail}
      data-testid={`card-piece-${item.id}`}
    >
      <div className="bg-gray-50 dark:bg-zinc-800 flex items-center justify-center" style={{ height: 160 }}>
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.nomFr} className="w-full h-full object-contain p-2" />
        ) : (
          <Package className="w-10 h-10 text-muted-foreground/30" />
        )}
      </div>

      <div className="p-3 flex flex-col flex-1 gap-2">
        <div className="flex flex-wrap gap-1">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeMeta.color}`}>
            {typeMeta.label}
          </span>
          <GarantieBadge sousGarantie={item.sousGarantie} />
        </div>

        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground leading-snug line-clamp-2">{item.nomFr}</p>
          <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{item.sku}</p>
        </div>

        {item.robotsCompatibles?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.robotsCompatibles.slice(0, 2).map(r => (
              <span key={r} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200/60 dark:border-blue-700/40">
                {r}
              </span>
            ))}
            {item.robotsCompatibles.length > 2 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                +{item.robotsCompatibles.length - 2}
              </span>
            )}
          </div>
        )}

        <div className="border-t border-border/60 pt-2 mt-auto">
          <p className={`text-sm font-bold ${item.prixAffiche !== null ? "text-foreground" : "text-muted-foreground italic text-xs"}`}>
            {formatPrix(item.prixAffiche)}
            {item.prixAffiche !== null && <span className="text-xs font-normal text-muted-foreground ml-1">HT</span>}
          </p>
        </div>

        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <QtySelector qty={qty} onChange={setQty} />
          <button
            onClick={() => addItem({
              productId: item.id,
              sku: item.sku,
              nomFr: item.nomFr,
              prixAffiche: item.prixAffiche,
              statutLabel: item.statutLabel,
              imageUrl: item.imageUrl,
            }, qty)}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: primary }}
            data-testid={`button-add-devis-card-${item.id}`}
          >
            <ShoppingCart className="w-3 h-3" />
            Devis
          </button>
        </div>
      </div>
    </div>
  );
}

function PieceDetailPanel({
  item, primary, onClose,
}: {
  item: PieceItem; primary: string; onClose: () => void;
}) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [zoomed, setZoomed] = useState(false);
  const typeMeta = TYPE_LABELS[item.type] ?? { label: item.type, color: "bg-muted text-muted-foreground" };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full max-w-xl h-full bg-card border-l border-border shadow-2xl overflow-y-auto flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="panel-piece-detail"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-lg font-semibold text-foreground truncate pr-4">{item.nomFr}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-secondary/60 text-muted-foreground flex-shrink-0"
            data-testid="button-close-piece-detail"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 p-6 space-y-5">
          {/* Photo grande taille avec zoom */}
          <div
            className={`relative group rounded-xl overflow-hidden bg-white dark:bg-zinc-900 border border-border flex items-center justify-center cursor-zoom-in transition-all ${zoomed ? "cursor-zoom-out" : ""}`}
            style={{ height: zoomed ? 400 : 220 }}
            onClick={() => setZoomed(z => !z)}
            data-testid="img-piece-zoom-toggle"
          >
            {item.imageUrl ? (
              <>
                <img
                  src={item.imageUrl}
                  alt={item.nomFr}
                  className="w-full h-full object-contain transition-all"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-black/40 rounded-full p-2">
                    <ZoomIn className="w-5 h-5 text-white" />
                  </div>
                </div>
              </>
            ) : (
              <Package className="w-20 h-20 text-muted-foreground/20" />
            )}
          </div>
          {zoomed && (
            <p className="text-xs text-center text-muted-foreground -mt-3">Cliquez pour réduire</p>
          )}

          {/* Infos principales */}
          <div>
            <p className="text-xs text-muted-foreground font-mono">{item.sku}</p>
            <h3 className="text-xl font-bold text-foreground mt-1">{item.nomFr}</h3>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeMeta.color}`}>
                {typeMeta.label}
              </span>
              <GarantieBadge sousGarantie={item.sousGarantie} />
            </div>
            {item.descriptionFr && (
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{item.descriptionFr}</p>
            )}
          </div>

          {/* Robots compatibles */}
          {item.robotsCompatibles?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Robots compatibles</p>
              <div className="flex flex-wrap gap-1.5">
                {item.robotsCompatibles.map(r => (
                  <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Prix */}
          <div className="bg-muted/50 rounded-xl p-4 border border-border">
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold text-foreground">{formatPrix(item.prixAffiche)}</span>
              {item.prixAffiche !== null && <span className="text-sm text-muted-foreground mb-1">HT</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Tarif {item.statutLabel}</p>
          </div>
        </div>

        {/* Footer sticky */}
        <div className="sticky bottom-0 p-4 border-t border-border bg-card space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Quantité</span>
            <QtySelector qty={qty} onChange={setQty} />
          </div>
          <button
            onClick={() => {
              addItem({
                productId: item.id,
                sku: item.sku,
                nomFr: item.nomFr,
                prixAffiche: item.prixAffiche,
                statutLabel: item.statutLabel,
                imageUrl: item.imageUrl,
              }, qty);
              onClose();
            }}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: primary }}
            data-testid="button-panel-add-to-devis"
          >
            <ShoppingCart className="w-4 h-4" />
            Ajouter au devis
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PiecesPage() {
  const { items, isLoading, error } = usePieces();
  const { brand } = usePortailAuth();
  const primary = brand?.couleurPrimaire || "#2563eb";

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [robotFilter, setRobotFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [selectedItem, setSelectedItem] = useState<PieceItem | null>(null);

  // Extraire la liste unique des robots compatibles depuis tous les produits
  const allRobots = useMemo(() => {
    const set = new Set<string>();
    items.forEach(p => (p.robotsCompatibles ?? []).forEach(r => set.add(r)));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter(p => {
      const matchSearch = !q || p.nomFr.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q);
      const matchType = typeFilter === "all" || p.type === typeFilter;
      const matchRobot = robotFilter === "all" || (p.robotsCompatibles ?? []).includes(robotFilter);
      return matchSearch && matchType && matchRobot;
    });
  }, [items, search, typeFilter, robotFilter]);

  return (
    <div className="flex flex-col h-full">
      {/* Header + filtres */}
      <div className="px-6 pt-6 pb-4 border-b border-border/60 bg-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-foreground mb-1">Pièces détachées & accessoires</h1>
            <p className="text-sm text-muted-foreground">Trouvez les pièces compatibles avec vos robots</p>
          </div>

          {/* Toggle Liste / Cartes */}
          <div className="flex items-center gap-1 border border-border rounded-lg p-0.5 flex-shrink-0">
            <button
              onClick={() => setViewMode("list")}
              className={`p-1.5 rounded-md transition-colors ${viewMode === "list" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="toggle-view-list"
              title="Vue liste"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded-md transition-colors ${viewMode === "grid" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="toggle-view-grid"
              title="Vue cartes"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          {/* Recherche */}
          <div className="relative flex-1 min-w-48 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou SKU…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="input-pieces-search"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Filtres types — non modifiés */}
          <div className="flex items-center gap-1.5">
            {[
              { value: "all", label: "Tous" },
              { value: "piece_detachee", label: "Pièces détachées" },
              { value: "consommable", label: "Consommables" },
              { value: "accessoire", label: "Accessoires" },
              { value: "iot", label: "IoT" },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setTypeFilter(opt.value)}
                className={`text-xs px-2.5 py-1.5 rounded-lg font-medium border transition-colors ${
                  typeFilter === opt.value
                    ? "text-white border-transparent"
                    : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`}
                style={typeFilter === opt.value ? { backgroundColor: primary, borderColor: primary } : {}}
                data-testid={`filter-type-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Filtre robot compatible */}
          {allRobots.length > 0 && (
            <div className="relative">
              <select
                value={robotFilter}
                onChange={e => setRobotFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-1.5 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
                data-testid="filter-robot"
              >
                <option value="all">Tous les robots</option>
                {allRobots.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
            </div>
          )}
        </div>
      </div>

      {/* Contenu */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-24 text-destructive">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <Wrench className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">Aucune pièce trouvée</p>
          </div>
        ) : viewMode === "list" ? (
          <div className="divide-y divide-border">
            {filtered.map(item => (
              <PieceRow
                key={item.id}
                item={item}
                primary={primary}
                onDetail={() => setSelectedItem(item)}
              />
            ))}
          </div>
        ) : (
          <div className="p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map(item => (
              <PieceCard
                key={item.id}
                item={item}
                primary={primary}
                onDetail={() => setSelectedItem(item)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Panneau détail */}
      {selectedItem && (
        <PieceDetailPanel
          item={selectedItem}
          primary={primary}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}
